import { computed, MaybeRefOrGetter, Ref, toValue, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useValidators } from '@/shared/composables/useValidators'
import { useValidation } from '@qicgroupdoha/qic-use'
import { GENDERS } from '@/shared/constants/other.constants'
import type { VisitorType } from '@/shared/types/store.types'

interface UseVisitorValidationOptions {
  person: Ref<VisitorType>
  selectedCountry: Ref<unknown>
  isDisabledProperties: boolean
  isHolder: boolean
  isMaritalStatusEnabled: MaybeRefOrGetter<boolean>
  isNotOcrManualMode: MaybeRefOrGetter<boolean>
  ocrUploadResult: MaybeRefOrGetter<'error' | 'success' | undefined>
  emailIsValid: MaybeRefOrGetter<boolean>
  passportMinDate: Ref<Date>
}

export const useVisitorValidation = (options: UseVisitorValidationOptions) => {
  const { t } = useI18n()
  const {
    relationshipValidator,
    required,
    nationalityValidator,
    countryValidator,
    emailValidator,
    withMessage,
    passportValidator,
  } = useValidators()

  const personValidation = computed(() => {
    const commonValidation: Record<string, unknown> = {
      firstName: { required },
      nationality: { nationalityValidator },
      country: { countryValidator },
      birthDate: { required },
      passportExpiryDate: {
        required,
        checkMinDate: withMessage(
          t('visitors.fill_visitors_info.file_recognition.passport_expiry_date'),
          (value: Nullable<Date | string>) =>
            value ? options.passportMinDate.value <= new Date(value) : true
        ),
      },
      passport: { ...passportValidator },
      email: {
        ...emailValidator,
        checkEmail: withMessage(t('qic_error_invalid_input'), () => toValue(options.emailIsValid)),
      },
    }
    const additionalValidation: Record<string, unknown> = {}
    if (options.isDisabledProperties) {
      additionalValidation.fullName = { required }
    } else {
      additionalValidation.lastName = { required }
    }
    if (!options.isHolder) {
      additionalValidation.relationship = { relationshipValidator }
    }
    if (toValue(options.isNotOcrManualMode)) {
      additionalValidation.uploadResult = {
        checkUploadResult: withMessage(
          t('visitors.fill_visitors_info.file_recognition.no_file_validation_error'),
          () => toValue(options.ocrUploadResult) !== 'error'
        ),
      }
    }
    if (toValue(options.isMaritalStatusEnabled)) {
      additionalValidation.maritalStatus = { required }
    }
    return {
      ...commonValidation,
      ...additionalValidation,
    }
  })

  const personValidationData = computed(() => {
    const data: Record<string, unknown> = { ...options.person.value }
    if (toValue(options.isNotOcrManualMode)) {
      data.uploadResult = toValue(options.ocrUploadResult)
    }
    return data
  })

  const { v$, errors } = useValidation(personValidation, personValidationData)

  const emailError = computed(() =>
    errors.email?.length > 0 ? [errors.email[0] as string] : []
  )

  const resetOcrData = (): void => {
    v$.value.$reset()
    options.person.value.lastName = ''
    options.person.value.firstName = ''
    options.person.value.passport = ''
    options.person.value.birthDate = null
    options.person.value.passportExpiryDate = null
    options.person.value.gender = GENDERS.MALE
  }

  // selectedCountry watcher: reset country validation when selectedCountry changes.
  // Person-state owns the person.country sync; this composable owns validation.
  watch(options.selectedCountry, () => {
    v$.value.country.$reset()
  })

  return {
    v$,
    errors,
    emailError,
    resetOcrData,
  }
}
